import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import {
    absoluteUrl,
    eligibleSets,
    nextReadyId,
    normalizeBackground,
    pickSet,
    rungUrl,
    slideObjectPosition,
    slidePositionCss,
    slidesFor,
    startRung,
    viewportTarget,
} from '@/background/sets';
import { BACKGROUND_SET_MESSAGE } from '@/blocks/preview';
import { useConnectionTier } from '@/hooks/useImageCap';
import { APPEARANCE_PREVIEW_QUERY } from '@/theme/tokens';

function isSlowLink() {
    if (typeof navigator === 'undefined') return false;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;
    return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
}

function isAppearancePreviewFrame() {
    if (typeof window === 'undefined' || window.parent === window) return false;
    if (document.documentElement?.dataset?.appearancePreview === '1') return true;
    try {
        return new URLSearchParams(window.location.search).get(APPEARANCE_PREVIEW_QUERY) === '1';
    } catch {
        return false;
    }
}

const SET_ATTR = 'data-bg-set';
const ORDER_ATTR = 'data-bg-order';
const CYCLE_ATTR = 'data-bg-cycle';
const SLOT_ATTR = 'data-bg-slot';
/**
 * Slides the loop opens with, low rung allowed. Later slides join only once
 * their sharp file is in, so the rotation grows without showing more blur.
 */
const POOL_START = 4;
/** How long the hand-over waits for the cycle's current slide to decode. */
const HANDOVER_WAIT_MS = 1000;
/** Sharp files downloading at once, nearest upcoming slide first. */
const SHARP_LANES = 4;
// The first background on the page hydrates over server HTML and must keep the
// set and order the server picked; later ones (client navigation) pick for themselves.
let hydrated = false;

function visitKey(set, files) {
    const images = (set?.images || []).filter((image) => image?.id && files?.[image.id]);
    return `${set?.id || ''}|${set?.order || ''}|${images.map((image) => image.id).join('\0')}`;
}

function serverVisit() {
    if (typeof document === 'undefined' || hydrated) return null;
    const frame = document.querySelector(`[${SET_ATTR}]`);
    if (!frame) return null;
    return {
        setId: frame.getAttribute(SET_ATTR),
        ids: (frame.getAttribute(ORDER_ATTR) || '').split(' ').filter(Boolean),
    };
}

/**
 * The set and slide order this visit plays. Both may be random, so the server
 * picks them and writes them on the frame; hydration reads them back so the
 * cycle the server HTML started keeps going.
 */
function initialVisit(eligible, files) {
    const fromServer = serverVisit();
    const set = (fromServer && eligible.find((entry) => entry.id === fromServer.setId))
        || (eligible.length > 1 ? pickSet(eligible, Math.random) : null)
        || eligible[0]
        || null;
    if (!set) return { setId: null, cache: { key: '', ids: [] } };
    const fresh = slidesFor(set, files).map((image) => image.id);
    const same = fromServer?.setId === set.id
        && fromServer.ids.length === fresh.length
        && [...fromServer.ids].sort().join(' ') === [...fresh].sort().join(' ');
    return { setId: set.id, cache: { key: visitKey(set, files), ids: same ? fromServer.ids : fresh } };
}

function pct(ms, total) {
    return `${Math.min(100, (ms / total) * 100).toFixed(4)}%`;
}

/** Keyframes for one slot of the server-drawn cycle: shown for one beat, then gone. */
function cycleKeyframes(name, count, intervalMs, fadeMs) {
    const total = count * intervalMs;
    const edge = 0.01;
    if (!fadeMs) {
        return `@keyframes ${name}{0%,${pct(intervalMs, total)}{opacity:1}${pct(intervalMs + edge, total)},100%{opacity:0}}`;
    }
    return `@keyframes ${name}{0%{opacity:0}${pct(fadeMs, total)},${pct(intervalMs + fadeMs, total)}{opacity:1}`
        + `${pct(intervalMs + fadeMs + edge, total)},100%{opacity:0}}`;
}

function decodeUrl(url, priority) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (priority) img.fetchPriority = priority;
        img.onload = () => {
            if (typeof img.decode === 'function') img.decode().then(() => resolve(img)).catch(() => resolve(img));
            else resolve(img);
        };
        img.onerror = () => reject(new Error('image'));
        img.src = url;
    });
}

function visitSlides(set, files, cache) {
    const images = (set?.images || []).filter((image) => image?.id && files?.[image.id]);
    const key = visitKey(set, files);
    if (cache.key !== key) {
        cache.key = key;
        cache.ids = (set ? slidesFor(set, files) : []).map((image) => image.id);
    }
    const byId = new Map(images.map((image) => [image.id, image]));
    return (cache.ids || []).map((id) => byId.get(id)).filter(Boolean);
}

// Softens the low rung's compression blocks until the sharp file covers it.
// Scales with the frame, which is a size container.
const LOW_BLUR = 'blur(max(3px, 0.4cqw))';

/**
 * A low-rung file, blurred. Blur fades a layer's own edges, so a plain copy
 * sits under it to keep the frame edges filled at the same crop.
 */
function SoftLayer({ position, url }) {
    return (
        <>
            <div style={layerStyle(position, url)} />
            <div style={{ ...layerStyle(position, url), filter: LOW_BLUR }} />
        </>
    );
}

SoftLayer.propTypes = {
    position: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
};

function layerStyle(position, url) {
    return {
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${JSON.stringify(url)})`,
        backgroundSize: 'cover',
        backgroundPosition: position,
        backgroundRepeat: 'no-repeat',
    };
}

/**
 * One slide. The low rung stays under any sharper file so upgrading never
 * blanks or shifts the crop — both layers share the same background-position.
 * CSS backgrounds are used instead of <img object-fit> so the browser never
 * paints a frame at the wrong crop while intrinsic size resolves.
 * `will-change: opacity` keeps a hidden layer rasterized, so the slide queued
 * next is already painted when its beat comes.
 */
function BackgroundLayer({
    slide,
    file,
    box,
    width,
    opacity,
    zIndex,
    fadeMs,
}) {
    const baseWidth = startRung();
    const baseUrl = absoluteUrl(rungUrl(file, baseWidth));
    const sharpUrl = width > baseWidth ? absoluteUrl(rungUrl(file, width)) : null;
    if (!slide || !baseUrl) return null;
    const position = slideObjectPosition(slide, file?.dimensions, box);

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                opacity,
                zIndex,
                willChange: 'opacity',
                transition: fadeMs ? `opacity ${fadeMs}ms linear` : 'none',
            }}
        >
            {sharpUrl && sharpUrl !== baseUrl ? (
                <>
                    <div style={layerStyle(position, baseUrl)} />
                    <div style={layerStyle(position, sharpUrl)} />
                </>
            ) : (
                <SoftLayer position={position} url={baseUrl} />
            )}
        </div>
    );
}

/**
 * The visit's low rungs cycling on CSS alone, drawn by the server so the
 * slideshow moves before script loads. The first slide sits underneath, so a
 * slot whose file has not arrived shows it rather than an empty frame.
 * Positions are worked out by CSS to match the measured crop, so handing over
 * to the live layers does not move anything.
 */
function CycleLayers({ slides, files, intervalMs, fadeMs, cycleRef }) {
    const first = slides[0];
    const firstUrl = first ? absoluteUrl(rungUrl(files?.[first.id], startRung())) : '';
    if (!firstUrl) return null;
    const count = slides.length;
    const total = count * intervalMs;
    const name = `bg-cycle-${count}-${Math.round(intervalMs)}-${Math.round(fadeMs)}`;
    return (
        <div ref={cycleRef} {...{ [CYCLE_ATTR]: '' }} style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
            {count > 1 ? (
                <style>
                    {`${cycleKeyframes(name, count, intervalMs, fadeMs)}`
                        + `@media (prefers-reduced-motion: reduce){[${CYCLE_ATTR}]>div{animation:none!important}}`}
                </style>
            ) : null}
            <div style={{ position: 'absolute', inset: 0 }}>
                <SoftLayer position={slidePositionCss(first, files?.[first.id]?.dimensions)} url={firstUrl} />
            </div>
            {count > 1 ? slides.map((slide, i) => {
                const file = files?.[slide.id];
                const url = absoluteUrl(rungUrl(file, startRung()));
                if (!url) return null;
                return (
                    <div
                        key={`${i}:${slide.id}`}
                        {...{ [SLOT_ATTR]: i }}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            animation: `${name} ${Math.round(total)}ms linear ${Math.round(i * intervalMs)}ms infinite`,
                        }}
                    >
                        <SoftLayer position={slidePositionCss(slide, file?.dimensions)} url={url} />
                    </div>
                );
            }) : null}
        </div>
    );
}

CycleLayers.propTypes = {
    slides: PropTypes.array.isRequired,
    files: PropTypes.object,
    intervalMs: PropTypes.number.isRequired,
    fadeMs: PropTypes.number.isRequired,
    cycleRef: PropTypes.object,
};

BackgroundLayer.propTypes = {
    slide: PropTypes.object,
    file: PropTypes.object,
    box: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
    width: PropTypes.number,
    opacity: PropTypes.number,
    zIndex: PropTypes.number,
    fadeMs: PropTypes.number,
};

/**
 * Full-viewport background. After mount, several enabled sets pick one at
 * random. The first slide to decode at the lowest rung is shown at once and
 * starts a fixed beat. On each beat the loop moves to the next slide that has
 * decoded, skipping any still loading, so rhythm never waits on the network.
 * Only the current, outgoing and upcoming slides are mounted; the upcoming one
 * sits rasterized at opacity 0 so its beat flips without a blank frame.
 * Once every low rung is in, sharper files load and paint over their slides.
 *
 * `backgroundImage.previewSetId` (Appearance play control) forces that set for
 * the visit. Re-stamping the same set is a no-op so play does not blank the loop.
 */
function FullscreenBackground({ backgroundImage, files }) {
    const forceSetId = backgroundImage?.previewSetId || null;
    const config = useMemo(() => normalizeBackground(backgroundImage), [backgroundImage]);
    const eligible = useMemo(() => {
        if (forceSetId && config) {
            const forced = config.sets.find((set) => (
                set.id === forceSetId
                && set.images.some((image) => image.id && files?.[image.id])
            ));
            if (forced) return [forced];
        }
        return eligibleSets(config, files);
    }, [config, files, forceSetId]);
    const eligibleKey = eligible.map((set) => set.id).join('\0');

    const [seed] = useState(() => initialVisit(eligible, files));
    const [chosenId, setChosenId] = useState(seed.setId);
    const pickedFor = useRef(eligibleKey);
    const [handedOver, setHandedOver] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [under, setUnder] = useState(null);
    const [reduced, setReduced] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [dropped, setDropped] = useState(() => new Set());
    const [ready, setReady] = useState(() => new Set());
    const [shownWidth, setShownWidth] = useState({});
    const [box, setBox] = useState({ width: 0, height: 0 });
    const { tier, cap: imageCap } = useConnectionTier();

    const frameRef = useRef(null);
    const orderCache = useRef(seed.cache);
    const cycleRef = useRef(null);
    // Where the live beat starts, so it carries on from the server-drawn cycle.
    const beatOrigin = useRef(null);
    const sharpInflight = useRef(new Map());
    const decodedWidth = useRef(new Map());
    const inflight = useRef(new Set());
    // Decoded Image objects stay referenced so the browser keeps their bitmaps.
    const held = useRef(new Map());
    const mounted = useRef(true);
    const beatRef = useRef({ shown: null, next: null, fadeMs: 0 });
    // Load results wait here and are published mid-beat, so new layers never
    // mount (and rasterize) right before a flip.
    const pendingReady = useRef(new Set());
    const pendingWidth = useRef({});
    const runningRef = useRef(false);

    const active = eligible.find((set) => set.id === chosenId) || eligible[0] || null;
    const visitKey = [
        active?.id || '',
        active?.order || '',
        (active?.images || []).map((image) => (
            `${image.id}:${image.align || ''}:${image.focal?.x ?? ''}:${image.focal?.y ?? ''}`
        )).join('\0'),
    ].join('|');
    const visit = useMemo(
        () => (active ? visitSlides(active, files, orderCache.current) : []),
        [visitKey, files],
    );
    const droppedKey = [...dropped].join('\0');
    const slides = useMemo(
        () => visit.filter((slide) => !dropped.has(slide.id)),
        [visit, droppedKey],
    );
    const slideKey = slides.map((slide) => slide.id).join('\0');
    const order = useMemo(() => slides.map((slide) => slide.id), [slideKey]);
    const byId = useMemo(() => new Map(slides.map((slide) => [slide.id, slide])), [slides]);
    // Cover crop math needs a real frame; painting before measure jumps the focal.
    const boxReady = box.width > 0 && box.height > 0;
    const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const fitWidth = viewportTarget(box.width, ratio);
    const sharpTarget = imageCap ? Math.min(fitWidth, imageCap) : fitWidth;
    const sharpOff = !boxReady || isSlowLink() || tier === 'slow' || sharpTarget <= startRung();
    const opening = useMemo(() => new Set(order.slice(0, POOL_START)), [order]);
    // Without sharp files there is nothing to wait for, so every slide plays.
    const inPool = (id) => sharpOff || opening.has(id) || id in shownWidth;
    const isReady = (id) => ready.has(id) && byId.has(id) && inPool(id);

    const shownId = currentId && isReady(currentId)
        ? currentId
        : order.find(isReady) || null;
    const nextId = shownId ? nextReadyId(order, shownId, isReady) : null;
    const intervalMs = Math.max(10, (active?.interval || 6) * 1000);
    const cut = reduced || active?.transition === 'cut';
    const fadeMs = cut ? 0 : Math.min(active?.transitionMs || 800, intervalMs);
    const cycleSlides = useMemo(() => (forceSetId ? [] : visit.slice(0, POOL_START)), [visit, forceSetId]);
    const cycling = cycleSlides.length > 0 && !handedOver;
    beatRef.current = { shown: shownId, next: nextId, fadeMs };

    useEffect(() => {
        mounted.current = true;
        hydrated = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const syncMotion = () => setReduced(motion.matches);
        syncMotion();
        motion.addEventListener?.('change', syncMotion);
        const syncHidden = () => setHidden(document.hidden);
        document.addEventListener('visibilitychange', syncHidden);
        return () => {
            motion.removeEventListener?.('change', syncMotion);
            document.removeEventListener('visibilitychange', syncHidden);
        };
    }, []);

    useEffect(() => {
        const el = frameRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(([entry]) => {
            const rect = entry.contentRect;
            const width = Math.round(rect.width);
            const height = Math.round(rect.height);
            setBox((prev) => (
                prev.width === width && prev.height === height
                    ? prev
                    : { width, height }
            ));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (pickedFor.current === eligibleKey) return;
        pickedFor.current = eligibleKey;
        if (eligible.length === 0) {
            setChosenId(null);
            return;
        }
        if (eligible.length === 1) {
            setChosenId(eligible[0].id);
            return;
        }
        const picked = pickSet(eligible, Math.random);
        setChosenId(picked?.id || eligible[0].id);
        setCurrentId(null);
        setUnder(null);
    }, [eligibleKey]);

    // Tell Appearance which set is looping so its play button can stay disabled.
    useEffect(() => {
        if (!isAppearancePreviewFrame()) return undefined;
        const setId = active?.id || null;
        if (!setId) return undefined;
        window.parent.postMessage(
            { type: BACKGROUND_SET_MESSAGE, setId },
            window.location.origin,
        );
        return undefined;
    }, [active?.id]);

    // Play control: load this set from the first slide when the forced set changes.
    // A second play of the same set is ignored (Appearance disables that button).
    useEffect(() => {
        if (!forceSetId) return;
        setChosenId(forceSetId);
        setCurrentId(null);
        setUnder(null);
        setDropped(new Set());
        pendingWidth.current = {};
        setShownWidth({});
    }, [forceSetId]);

    const publish = (sync) => {
        const apply = () => {
            setReady((prev) => (
                prev.size === pendingReady.current.size ? prev : new Set(pendingReady.current)
            ));
            setShownWidth((prev) => {
                const next = pendingWidth.current;
                const same = Object.keys(next).length === Object.keys(prev).length
                    && Object.keys(next).every((id) => prev[id] === next[id]);
                return same ? prev : { ...next };
            });
        };
        if (sync) flushSync(apply);
        else apply();
    };

    // Before playback (or while paused) there is no beat to protect: show at once.
    const settle = () => {
        if (!mounted.current) return;
        if (!runningRef.current || !beatRef.current.shown) publish(false);
    };

    const drop = (ids) => {
        if (!ids.length) return;
        setDropped((prev) => {
            const next = new Set(prev);
            let changed = false;
            ids.forEach((id) => {
                if (!prev.has(id)) {
                    next.add(id);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    };

    // Low rungs load in visit order; each slide joins the loop the moment it decodes.
    useEffect(() => {
        const low = startRung();
        const missing = [];
        order.forEach((id, i) => {
            if (pendingReady.current.has(id) || inflight.current.has(id)) return;
            const url = absoluteUrl(rungUrl(files?.[id], low));
            if (!url) {
                missing.push(id);
                return;
            }
            inflight.current.add(id);
            decodeUrl(url, i < 2 ? 'high' : 'low').then((img) => {
                held.current.set(`${id}:${low}`, img);
                decodedWidth.current.set(id, Math.max(decodedWidth.current.get(id) || 0, low));
                pendingReady.current.add(id);
                settle();
            }).catch(() => {
                if (mounted.current) drop([id]);
            }).finally(() => {
                inflight.current.delete(id);
            });
        });
        drop(missing);
        return undefined;
    }, [slideKey, files]);

    // Take over from the server-drawn cycle on the slide it is showing, as soon
    // as that slide has decoded here, keeping its place in the beat.
    useEffect(() => {
        if (!cycling || !boxReady) return undefined;
        const ids = cycleSlides.map((slide) => slide.id);
        const startedAt = performance.now();
        let raf;
        const check = () => {
            const animation = cycleRef.current
                ?.querySelector(`[${SLOT_ATTR}="0"]`)
                ?.getAnimations?.()[0];
            const elapsed = Number(animation?.currentTime);
            const timed = Number.isFinite(elapsed) && ids.length > 1 && !reduced;
            const target = ids[timed ? Math.floor(elapsed / intervalMs) % ids.length : 0];
            const decoded = pendingReady.current;
            const late = performance.now() - startedAt > HANDOVER_WAIT_MS;
            if (decoded.has(target) || (late && ids.some((id) => decoded.has(id)))) {
                const pick = decoded.has(target) ? target : ids.find((id) => decoded.has(id));
                beatOrigin.current = timed && pick === target ? performance.now() - (elapsed % intervalMs) : null;
                publish(false);
                setCurrentId(pick || null);
                setHandedOver(true);
                return;
            }
            raf = requestAnimationFrame(check);
        };
        raf = requestAnimationFrame(check);
        return () => cancelAnimationFrame(raf);
    }, [cycling, boxReady, cycleSlides, intervalMs, reduced, order]);

    // Fixed beat anchored to when playback starts, checked every animation frame
    // so flips land on the first frame past each boundary and commit
    // synchronously. Missed beats are skipped, never bunched; a beat with
    // nothing new ready holds. Half-way between beats, load results publish.
    const running = Boolean(shownId) && boxReady && !reduced && !hidden && order.length > 1 && !cycling;
    runningRef.current = running;
    useEffect(() => {
        if (!running) {
            publish(false);
            return undefined;
        }
        const halfMs = intervalMs / 2;
        let start = null;
        let lastHalf = 0;
        let owePublish = false;
        let raf;
        let prev = null;
        let frameMs = 1000 / 60;
        const frame = (now) => {
            if (start === null) {
                start = beatOrigin.current ?? now;
                beatOrigin.current = null;
            }
            if (prev !== null) {
                const delta = now - prev;
                if (delta > 4 && delta < 50) frameMs += (delta - frameMs) * 0.1;
            }
            prev = now;
            // Boundaries usually coincide with frame timestamps; half a frame of
            // slack stops timestamp noise from flipping one frame early or late.
            const half = Math.floor((now - start + frameMs / 2) / halfMs);
            if (half > lastHalf) {
                const beatCrossed = Math.floor(half / 2) > Math.floor(lastHalf / 2);
                lastHalf = half;
                if (beatCrossed) {
                    const { shown, next, fadeMs: fade } = beatRef.current;
                    if (next && next !== shown) {
                        flushSync(() => {
                            setUnder(fade ? shown : null);
                            setCurrentId(next);
                        });
                    }
                    owePublish = half % 2 === 1;
                } else {
                    owePublish = true;
                }
            } else if (owePublish) {
                owePublish = false;
                publish(true);
            }
            raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
        return () => cancelAnimationFrame(raf);
    }, [running, intervalMs, active?.id]);

    useEffect(() => {
        if (!under) return undefined;
        const timer = setTimeout(() => setUnder(null), fadeMs);
        return () => clearTimeout(timer);
    }, [under, shownId, fadeMs]);

    // Sharper files start as soon as the frame is measured, a few at a
    // time: the opening slides first (from the one on screen), then the rest
    // in playing order. Each paints over its slide, or joins the loop, as it
    // arrives; one that fails joins at the low rung.
    useEffect(() => {
        if (sharpOff) return undefined;
        const target = sharpTarget;
        let cancel = false;
        const head = order.slice(0, POOL_START);
        const from = Math.max(0, head.indexOf(beatRef.current.shown));
        const queue = [...head.slice(from), ...head.slice(0, from), ...order.slice(POOL_START)];
        let started = 0;
        const raise = (id, width = target) => {
            if ((pendingWidth.current[id] || 0) >= width) return;
            pendingWidth.current = { ...pendingWidth.current, [id]: width };
            settle();
        };
        const next = () => {
            while (!cancel && queue.length) {
                const id = queue.shift();
                if ((decodedWidth.current.get(id) || 0) >= target) {
                    raise(id);
                    continue;
                }
                const file = files?.[id];
                const url = absoluteUrl(rungUrl(file, target));
                if (!url || url === absoluteUrl(rungUrl(file, startRung()))) {
                    raise(id, startRung());
                    continue;
                }
                const job = sharpInflight.current.get(url) || decodeUrl(url, started < SHARP_LANES ? 'high' : 'auto');
                started += 1;
                sharpInflight.current.set(url, job);
                job.then((img) => {
                    held.current.set(`${id}:${target}`, img);
                    decodedWidth.current.set(id, Math.max(decodedWidth.current.get(id) || 0, target));
                    if (!cancel && mounted.current) raise(id);
                }).catch(() => {
                    if (!cancel && mounted.current) raise(id, startRung());
                }).finally(() => {
                    sharpInflight.current.delete(url);
                    next();
                });
                return;
            }
        };
        for (let lane = 0; lane < SHARP_LANES; lane += 1) next();
        return () => { cancel = true; };
    }, [sharpOff, sharpTarget, slideKey, files]);

    const widthFor = (id) => shownWidth[id] || startRung();
    const fading = Boolean(under && under !== shownId && fadeMs);
    const layers = boxReady && !cycling
        ? [...new Set([shownId, fading ? under : null, nextId].filter(Boolean))]
        : [];
    const preloads = cycleSlides
        .slice(0, 3)
        .map((slide) => absoluteUrl(rungUrl(files?.[slide.id], startRung())))
        .filter(Boolean);

    return (
        <div
            ref={frameRef}
            className="fs-media absolute inset-0 overflow-hidden pointer-events-none"
            style={{ zIndex: 0, containerType: 'size' }}
            {...{
                [SET_ATTR]: active?.id || undefined,
                [ORDER_ATTR]: visit.length ? visit.map((slide) => slide.id).join(' ') : undefined,
            }}
            aria-hidden="true"
            data-page-path="page.background"
        >
            {preloads.length ? (
                <Helmet>
                    {preloads.map((url, i) => (
                        <link
                            key={url}
                            rel="preload"
                            as="image"
                            href={url}
                            fetchPriority={i === 0 ? 'high' : undefined}
                        />
                    ))}
                </Helmet>
            ) : null}
            {cycling ? (
                <CycleLayers
                    slides={cycleSlides}
                    files={files}
                    intervalMs={intervalMs}
                    fadeMs={fadeMs}
                    cycleRef={cycleRef}
                />
            ) : null}
            {layers.map((id) => {
                const isShown = id === shownId;
                const isUnder = fading && id === under;
                return (
                    <BackgroundLayer
                        key={id}
                        slide={byId.get(id)}
                        file={files?.[id]}
                        box={box}
                        width={widthFor(id)}
                        opacity={isShown || isUnder ? 1 : 0}
                        zIndex={isShown ? 2 : isUnder ? 1 : 0}
                        fadeMs={isShown && fading ? fadeMs : 0}
                    />
                );
            })}
        </div>
    );
}

FullscreenBackground.propTypes = {
    backgroundImage: PropTypes.object,
    files: PropTypes.object,
};

export default FullscreenBackground;
